import {
  delBb,
  delGrid,
  delMartinStrategy,
  delSj,
  delUb,
  deleteFuturesX,
  deleteNewStrategy,
  deleteSupremeHedge,
  deleteTermHedging,
  getAllSymbolsByCoin,
  getIndex,
  getMartinStrategy,
  modifyBb,
  modifyFuturesSpace,
  modifyFuturesXSpace,
  modifyNewStrategy,
  modify_term_hedging,
  pauseFuturesSpotAI,
  startFuturesSpotAI,
  startOrPauseGridStrategy,
  startOrPauseMartinStrategy,
  startOrPauseNewStrategy,
  startStopBb,
  startSupremeHedge,
  stopSupremeHedge
} from '@/server/robot'
import { userAlterMartin } from '@/server/martin'

function finiteNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

function numberOr(value, fallback) {
  const result = finiteNumber(value)
  return result === null ? fallback : result
}

function firstValue(object, keys, fallback = '') {
  for (const key of keys) {
    const value = object && object[key]
    if (value !== '' && value !== null && value !== undefined) return value
  }
  return fallback
}

export function robotProductType(robot) {
  return String(robot && robot.productType || '').toLowerCase()
}

export function robotTypeValue(robot) {
  const raw = robot && robot.raw || {}
  const direct = finiteNumber(firstValue(raw, ['__robotType', 'robot_type']))
  if (direct !== null) return direct
  const code = String(firstValue(raw, ['robot_type'])).toLowerCase()
  if (code === 'bn') return 0
  if (code === 'ok') return 1
  if (code === 'gate') return 2
  if (code === 'bybit') return 3
  return 0
}

function selectExchange(robot) {
  const robotType = robotTypeValue(robot)
  getIndex(robotType === 0 ? 1 : 2)
  return robotType
}

function strategySid(robot) {
  const raw = robot.raw || {}
  return firstValue(raw, ['sid', 'strategy_uid', 'uid', 'id'], robot.id)
}

function strategyUid(robot) {
  const raw = robot.raw || {}
  return firstValue(raw, ['strategy_uid', 'uid', 'sid', 'id'], robot.id)
}

function strategyCoin(robot) {
  const raw = robot.raw || {}
  return firstValue(raw, ['coin_two', 'base_coin', 'coin', 'symbol'])
}

function strategyVersion(robot) {
  const raw = robot.raw || {}
  return firstValue(raw, ['old_new', 'version', 'version_type', 'versionType'])
}

function parseJsonArray(value) {
  if (Array.isArray(value)) return value
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

function buildMartinConfig(strategyType, billNumber) {
  const count = Math.max(1, Math.min(31, Math.floor(Number(billNumber) || 1) - 1))
  const policyType = Number(strategyType) + 1
  const replenishmentConf = []
  const replenishmentCallback = []
  for (let index = 1; index <= count; index += 1) {
    if (policyType === 1) {
      const previous = replenishmentConf[index - 2]
      let decline = 1
      if (index > 1 && index <= 3) decline = 1 + Number(previous.decline)
      if (index >= 4 && index <= 5) decline = 1.5 + Number(previous.decline)
      if (index === 6 || index >= 8) decline = 3.5 + Number(previous.decline)
      if (index === 7) decline = 4.5 + Number(previous.decline)
      replenishmentConf.push({
        decline,
        multiple: index === 1 ? 2 : Number(previous.multiple) * 2,
        id: index
      })
      replenishmentCallback.push({ backValue: 0.15, id: index })
    } else {
      replenishmentConf.push({ decline: policyType, multiple: 1, id: index })
      replenishmentCallback.push({ backValue: 1, id: index })
    }
  }
  return { replenishmentConf, replenishmentCallback }
}

export function robotActionCapabilities(robot) {
  const type = robotProductType(robot)
  return {
    modify: ['astra', 'futures_x', 'hedge', 'term', 'bb'].includes(type),
    pauseResume: ['astra', 'hedge', 'term', 'bb'].includes(type),
    delete: ['astra', 'futures_x', 'hedge', 'term', 'bb', 'grid', 'martin', 'sj', 'ub'].includes(type)
  }
}

export function robotNeedsMaximumRestart(robot) {
  return (
    ['hedge', 'term'].includes(robotProductType(robot)) &&
    Number(robot && robot.raw && robot.raw.stop_reason) === 1
  )
}

export function minimumRestartPosition(robot) {
  const raw = robot && robot.raw || {}
  const used = numberOr(firstValue(raw, ['max_used']), 0)
  const totalSpace = numberOr(
    firstValue(raw, ['totalSpace', 'total_space', 'space']),
    0
  )
  return Number((used + totalSpace * 0.3).toFixed(0))
}

function bbMinimumPosition(item, coin, robotType) {
  if (!item) return null
  const target = String(item.target_coin || '')
  const cost = String(item.cost_coin || '')
  const symbol = String(coin || '')
  let source = null
  if (Number(robotType) === 1) {
    if (symbol === `${target}-${cost}`) source = finiteNumber(item.min_qty)
    if (symbol === `${cost}-${target}`) source = finiteNumber(item.min_qty_true)
  } else if (Number(robotType) === 0) {
    if (symbol === `${target}-${cost}`) source = finiteNumber(item.min_qty_true)
    if (symbol === `${cost}-${target}`) source = finiteNumber(item.min_notional)
  }
  return source === null ? null : Number((source * 200).toFixed(8))
}

async function loadBbActionDetail(robot) {
  const raw = robot.raw || {}
  const coinOne = String(firstValue(raw, ['coin_one']))
  const coinTwo = String(firstValue(raw, ['coin_two']))
  const robotType = selectExchange(robot)
  const response = await getAllSymbolsByCoin(coinOne)
  if (!response || response.success === false) {
    throw new Error((response && response.message) || '币币策略最小仓位读取失败')
  }
  const rows = Array.isArray(response.data) ? response.data : []
  const pair = rows.find(item => {
    const target = String(item && item.target_coin || '')
    const cost = String(item && item.cost_coin || '')
    return (
      (target === coinOne && cost === coinTwo) ||
      (target === coinTwo && cost === coinOne)
    )
  })
  if (!pair) throw new Error('未找到当前币币策略的交易对配置')
  return {
    ...robot,
    raw: {
      ...raw,
      __minPositionOne: bbMinimumPosition(pair, `${coinOne}-${coinTwo}`, robotType),
      __minPositionTwo: bbMinimumPosition(pair, `${coinTwo}-${coinOne}`, robotType)
    }
  }
}

export async function loadRobotActionDetail(robot) {
  const type = robotProductType(robot)
  if (type === 'bb') return loadBbActionDetail(robot)
  if (type !== 'martin') return robot
  const response = await getMartinStrategy(strategyUid(robot))
  if (!response || response.success === false) {
    throw new Error((response && response.message) || '马丁策略详情读取失败')
  }
  return {
    ...robot,
    raw: {
      ...(robot.raw || {}),
      ...(response.data || {})
    }
  }
}

export function defaultRobotActionForm(robot, action = 'modify') {
  const raw = robot && robot.raw || {}
  const type = robotProductType(robot)
  const amount = type === 'term'
    ? firstValue(raw, ['totalSpace', 'total_space', 'space'])
    : firstValue(raw, ['space', 'totalSpace', 'total_space', 'fund'])
  const maxPosition = firstValue(raw, ['max_used', 'maxPosition', 'max_position'], '0')
  const martinStrategyType = numberOr(firstValue(raw, ['strategy_type']), 0)
  const martinBillNumber = numberOr(firstValue(raw, ['billing_quantity']), 10)
  return {
    action,
    confirmPauseRisk: false,
    amount: amount === '' ? '' : String(amount),
    amountOne: String(firstValue(raw, ['space', 'space_one'], '')),
    amountTwo: String(firstValue(raw, ['space_two'], '')),
    stopLoss: numberOr(firstValue(raw, ['stop_loss']), 0),
    lossTransferRate: String(firstValue(raw, ['loss_transfer_rate'], '0')),
    positionProportion: String(firstValue(raw, ['position_proportion'], '70')),
    infinite: String(maxPosition) === '0',
    maxPosition: String(maxPosition) === '0' ? '' : String(maxPosition),
    orderMode: type === 'term' ? '1' : '3',
    strategyType: martinStrategyType,
    trendCycle: numberOr(firstValue(raw, ['trend_cycle']), 1),
    firstSpace: String(firstValue(raw, ['space', 'first_space'], '15')),
    cycleNumber: String(firstValue(raw, ['number_of_cycles', 'cycle_number'], '0')),
    firstMultiple: String(firstValue(raw, ['first_single_multiple', 'first_multiple'], '5')),
    billNumber: String(martinBillNumber),
    stopRatio: String(firstValue(raw, ['stop_ratio'], '1.5')),
    stopCallback: String(firstValue(raw, ['interference_stop_callback', 'stop_callback'], '0.2')),
    trackStop: numberOr(firstValue(raw, ['tracking_stop', 'track_stop']), 0),
    trackReplenishment: numberOr(firstValue(raw, ['track_replenishment']), 1),
    fullStop: numberOr(firstValue(raw, ['full_stop']), 1),
    fullStopRatio: String(firstValue(raw, ['stop_loss_ratio', 'full_stop_ratio'], '5')),
    trendCycleSwitch: numberOr(firstValue(raw, ['trend_cycle_switch']), 0),
    replenishmentConf: parseJsonArray(firstValue(raw, ['replenishment_conf_new', 'replenishment_conf'])),
    replenishmentCallback: parseJsonArray(firstValue(raw, ['replenishment_callback_new', 'replenishment_callback']))
  }
}

export async function executeRobotAction({ action, robot, form = {} }) {
  const type = robotProductType(robot)
  const robotType = selectExchange(robot)
  const raw = robot.raw || {}
  const sid = strategySid(robot)
  const uid = strategyUid(robot)

  if (action === 'pause') {
    if (type === 'bb') {
      return startStopBb(
        robotType,
        2,
        firstValue(raw, ['coin_one']),
        firstValue(raw, ['coin_two'])
      )
    }
    if (type === 'grid') return startOrPauseGridStrategy(uid, 2)
    if (type === 'martin') return startOrPauseMartinStrategy(uid, 2)
    if (type === 'astra') return startOrPauseNewStrategy(robotType, sid, 2)
    if (type === 'futures_x') return stopSupremeHedge(robotType, sid)
    if (type === 'term') {
      return pauseFuturesSpotAI(strategyCoin(robot), robotType, strategyVersion(robot))
    }
    if (type === 'hedge') return stopSupremeHedge(robotType, sid)
  }

  if (action === 'resume') {
    if (type === 'bb') {
      return startStopBb(
        robotType,
        1,
        firstValue(raw, ['coin_one']),
        firstValue(raw, ['coin_two'])
      )
    }
    if (type === 'grid') return startOrPauseGridStrategy(uid, 1)
    if (type === 'martin') return startOrPauseMartinStrategy(uid, 1)
    if (type === 'astra') return startOrPauseNewStrategy(robotType, sid, 1)
    if (type === 'futures_x') return startSupremeHedge({ robotType, sid })
    if (type === 'term') {
      const params = {
        coin: strategyCoin(robot),
        robot_type: robotType,
        version: strategyVersion(robot)
      }
      if (Number(raw.stop_reason) === 1) {
        params.maxPosition = form.infinite ? '0' : form.maxPosition
      }
      return startFuturesSpotAI(params)
    }
    if (type === 'hedge') {
      const params = { robotType, sid }
      if (Number(raw.stop_reason) === 1) {
        params.maxPosition = form.infinite ? '0' : form.maxPosition
      }
      return startSupremeHedge(params)
    }
  }

  if (action === 'delete') {
    if (type === 'sj') return delSj(firstValue(raw, ['group_id', 'belong_to2', 'id']))
    if (type === 'ub') return delUb(firstValue(raw, ['id', 'uid']))
    const robotStopped = Number(raw.__robotState) === 2
    if (type === 'bb') {
      return delBb(
        firstValue(raw, ['coin', 'coin_one']),
        robotStopped ? '2' : (form.orderMode || '3')
      )
    }
    if (type === 'grid') return delGrid(uid, robotStopped ? '2' : (form.orderMode || '3'))
    if (type === 'martin') {
      return delMartinStrategy(uid, robotStopped ? '2' : (form.orderMode || '3'))
    }
    if (type === 'astra') return deleteNewStrategy(robotType, sid)
    if (type === 'futures_x') return deleteFuturesX({ robotType, sid })
    if (type === 'hedge') return deleteSupremeHedge(robotType, sid)
    if (type === 'term') {
      return deleteTermHedging(
        strategyCoin(robot),
        robotType,
        strategyVersion(robot),
        form.orderMode || '1'
      )
    }
  }

  if (action === 'modify') {
    const amount = finiteNumber(form.amount)
    if (type === 'astra') return modifyNewStrategy(robotType, amount, sid)
    if (type === 'futures_x') {
      const storedStopLoss = finiteNumber(form.stopLoss)
      const stopLoss = storedStopLoss === 5
        ? 1
        : storedStopLoss === 100
          ? 2
          : (storedStopLoss || 0)
      return modifyFuturesXSpace({
        robotType,
        sid,
        space: amount,
        stopLoss
      })
    }
    if (type === 'hedge') {
      return modifyFuturesSpace(
        robotType,
        amount,
        sid,
        finiteNumber(form.stopLoss) || 0,
        finiteNumber(form.lossTransferRate) || 0,
        finiteNumber(form.positionProportion) || 70,
        form.infinite ? '0' : form.maxPosition
      )
    }
    if (type === 'term') {
      return modify_term_hedging({
        space: amount,
        robot_type: robotType,
        version: strategyVersion(robot),
        coin: strategyCoin(robot),
        version_number: firstValue(raw, ['version_number']),
        maxPosition: form.infinite ? '0' : form.maxPosition
      })
    }
    if (type === 'bb') {
      return modifyBb(
        firstValue(raw, ['coin_one']),
        firstValue(raw, ['coin_two']),
        form.amountOne,
        form.amountTwo
      )
    }
    if (type === 'martin') {
      const billNumber = finiteNumber(form.billNumber)
      const firstSpace = finiteNumber(form.firstSpace)
      const config = buildMartinConfig(form.strategyType, billNumber)
      const fullSpace = config.replenishmentConf.reduce(
        (sum, item) => sum + Number(item.multiple) * firstSpace,
        firstSpace * finiteNumber(form.firstMultiple)
      )
      return userAlterMartin(
        robotType,
        firstValue(raw, ['quote_coin', 'coin_one'], 'USDT'),
        firstValue(raw, ['base_coin', 'coin_two']),
        finiteNumber(form.strategyType),
        finiteNumber(form.trendCycle),
        firstSpace,
        finiteNumber(form.cycleNumber),
        finiteNumber(form.firstMultiple),
        billNumber,
        finiteNumber(form.stopRatio),
        finiteNumber(form.stopCallback),
        finiteNumber(form.trackStop),
        JSON.stringify(config.replenishmentConf),
        JSON.stringify(config.replenishmentCallback),
        finiteNumber(form.trackReplenishment),
        finiteNumber(form.fullStop),
        finiteNumber(form.fullStopRatio),
        fullSpace,
        uid,
        finiteNumber(form.trendCycleSwitch)
      )
    }
  }

  throw new Error('该策略没有可用的对应接口')
}
